import type { FC, ReactNode } from 'react'
import type { Category, StoreSettings } from '@/types'

import Link from 'next/link'
import Image from 'next/image'
import {
    Clock,
    Facebook,
    Instagram,
    Mail,
    MapPin
} from 'lucide-react'
import { TikTokIcon, WhatsAppIcon } from '@/components/generals/brand-icons'
import { socialUrl, whatsappLink } from '@/lib/format'

import {
    LOGO_HEIGHT,
    LOGO_SRC,
    LOGO_WIDTH
} from '@/constants/site'

interface StoreFooterProps {
    settings: StoreSettings
    categories: Array<Category>
    year: number
}

export const StoreFooter: FC<StoreFooterProps> = ({ settings, categories, year }): ReactNode => {
    const wa = whatsappLink(settings.whatsapp, `Halo ${settings.store_name}, saya mau tanya soal camilannya.`)
    const socials = [
        { key: 'instagram', label: 'Instagram', href: socialUrl('instagram', settings.instagram), icon: <Instagram className='size-5' /> },
        { key: 'tiktok', label: 'TikTok', href: socialUrl('tiktok', settings.tiktok), icon: <TikTokIcon className='size-[18px]' /> },
        { key: 'facebook', label: 'Facebook', href: socialUrl('facebook', settings.facebook), icon: <Facebook className='size-5' /> }
    ].filter(social => social.href)
    const hasContact = Boolean(wa || settings.email || settings.address || settings.opening_hours)

    return (
        <footer id='kontak' className='relative mt-24 bg-brand-choco text-brand-cream'>
            <svg className='absolute -top-[46px] left-0 h-12 w-full text-brand-choco' viewBox='0 0 1440 48' preserveAspectRatio='none' aria-hidden='true'>
                <path fill='currentColor' d='M0 48h1440V20c-120 18-240 26-360 18S840 4 720 6 480 34 360 36 120 24 0 12z' />
            </svg>

            <div className='bg-dots-light'>
                <div className='mx-auto grid max-w-7xl gap-12 px-4 pb-12 pt-14 sm:px-6 md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1.2fr] lg:px-8'>
                    <div>
                        <div className='inline-flex rounded-[26px] border-2 border-brand-yellow bg-white p-2.5 shadow-[0_6px_0_0_#F28C1B]'>
                            <Image src={LOGO_SRC} alt='Logo GEMAR JAJAN' width={LOGO_WIDTH} height={LOGO_HEIGHT} sizes='96px' className='h-28 w-auto' />
                        </div>

                        {settings.description && (
                            <p className='mt-6 max-w-sm leading-relaxed text-brand-cream/80' data-mdlfy-cms={`store_settings:${settings.id}:description`}>
                                {settings.description}
                            </p>
                        )}

                        {socials.length > 0 && (
                            <ul className='mt-6 flex gap-3' aria-label='Media sosial'>
                                {socials.map(social => (
                                    <li key={social.key}>
                                        <a
                                            href={social.href as string}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            className='flex size-11 items-center justify-center rounded-full border-2 border-brand-cream/25 text-brand-cream transition-colors hover:border-brand-yellow hover:bg-brand-yellow hover:text-brand-choco'
                                            aria-label={`${social.label} ${settings.store_name}`}
                                        >
                                            {social.icon}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <nav aria-label='Kategori produk'>
                        <h2 className='font-heading text-lg font-semibold text-brand-yellow'>
                            Kategori
                        </h2>
                        <ul className='mt-5 space-y-2.5'>
                            {categories.map(category => (
                                <li key={category.id}>
                                    <Link href={`/produk?kategori=${category.slug}`} className='text-brand-cream/80 transition-colors hover:text-brand-yellow'>
                                        <span data-mdlfy-cms={`categories:${category.id}:name`}>{category.name}</span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </nav>

                    <nav aria-label='Belanja'>
                        <h2 className='font-heading text-lg font-semibold text-brand-yellow'>
                            Belanja
                        </h2>
                        <ul className='mt-5 space-y-2.5 text-brand-cream/80'>
                            <li><Link href='/produk' className='transition-colors hover:text-brand-yellow'>Semua Produk</Link></li>
                            <li><Link href='/#promo' className='transition-colors hover:text-brand-yellow'>Promo Hari Ini</Link></li>
                            <li><Link href='/#rekomendasi' className='transition-colors hover:text-brand-yellow'>Bantu Pilih Camilan</Link></li>
                            <li><Link href='/#cara-pesan' className='transition-colors hover:text-brand-yellow'>Cara Pesan</Link></li>
                            <li><Link href='/keranjang' className='transition-colors hover:text-brand-yellow'>Keranjang Belanja</Link></li>
                        </ul>
                    </nav>

                    <div>
                        <h2 className='font-heading text-lg font-semibold text-brand-yellow'>
                            Hubungi Kami
                        </h2>
                        <ul className='mt-5 space-y-4 text-brand-cream/85'>
                            {wa && (
                                <li>
                                    <a href={wa} target='_blank' rel='noopener noreferrer' className='group flex items-start gap-3'>
                                        <span className='flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-green text-white'>
                                            <WhatsAppIcon className='size-[18px]' />
                                        </span>
                                        <span>
                                            <span className='block text-xs uppercase tracking-wider text-brand-cream/60'>WhatsApp</span>
                                            <span className='font-semibold group-hover:text-brand-yellow' data-mdlfy-cms={`store_settings:${settings.id}:whatsapp`}>{settings.whatsapp}</span>
                                        </span>
                                    </a>
                                </li>
                            )}
                            {settings.email && (
                                <li className='flex items-start gap-3'>
                                    <Mail className='mt-0.5 size-5 shrink-0 text-brand-yellow' aria-hidden='true' />
                                    <a href={`mailto:${settings.email}`} className='break-all hover:text-brand-yellow' data-mdlfy-cms={`store_settings:${settings.id}:email`}>
                                        {settings.email}
                                    </a>
                                </li>
                            )}
                            {settings.address && (
                                <li className='flex items-start gap-3'>
                                    <MapPin className='mt-0.5 size-5 shrink-0 text-brand-yellow' aria-hidden='true' />
                                    <span className='whitespace-pre-line' data-mdlfy-cms={`store_settings:${settings.id}:address`}>
                                        {settings.address}
                                    </span>
                                </li>
                            )}
                            {settings.opening_hours && (
                                <li className='flex items-start gap-3'>
                                    <Clock className='mt-0.5 size-5 shrink-0 text-brand-yellow' aria-hidden='true' />
                                    <span data-mdlfy-cms={`store_settings:${settings.id}:opening_hours`}>
                                        {settings.opening_hours}
                                    </span>
                                </li>
                            )}
                            {!hasContact && (
                                <li className='leading-relaxed text-brand-cream/75'>
                                    Pilih camilan favoritmu, lalu kirim pesanan lewat halaman keranjang. Admin kami akan segera menghubungimu.
                                </li>
                            )}
                        </ul>
                    </div>
                </div>

                <div className='border-t border-brand-cream/10'>
                    <div className='mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-6 text-sm text-brand-cream/60 sm:flex-row sm:px-6 lg:px-8'>
                        <p>
                            &copy; {year} <span data-mdlfy-cms={`store_settings:${settings.id}:store_name`}>{settings.store_name}</span>. Semua hak dilindungi.
                        </p>
                        <Link href='/admin' className='transition-colors hover:text-brand-yellow'>
                            Masuk admin
                        </Link>
                    </div>
                </div>
            </div>
        </footer>
    )
}
